import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import useWorkstationCookie from '@/hooks/common/useWorkstationCookie';
import store from '@/store';

import { rejectRouteHandler } from './routeMiddleware';
import router from '@/router';

const ALLOWED_NEW_EXCEPTIONS = [
  'BankAccountTran',
];

// eslint-disable-next-line max-lines-per-function
const beforeInterceptor = async (to, from, next) => {
  // Make sure state is hydrated before checking
  await store.restored;
  const username = store.getters['auth/username'];
  const previousUsername = store.getters['auth/previousUsername'];
  const hasPerm = store.getters['permissions/isPermPresent'];
  const isPreviousUser = previousUsername === username;
  const fromNewOrEdit = from && from.name && (from.name.endsWith('_edit_screen') || from.name.endsWith('_new_screen'));
  const className = _get(to, 'params.classname', '') || _get(to, 'meta.classname', '');
  const cacheDirtyTrackingFlag = fromNewOrEdit && className && store.getters['cached/canCacheClass'](className);
  let isAuthenticated = store.getters['auth/authenticated'];
  let payload = {};

  const {
    hasRequiredWorkstationData,
    getWorkstationCookie,
    setWorkstationCookie,
    deleteWorkstationCookie,
  } = useWorkstationCookie();
  const shouldSaveSiteAndDrawer = store.getters['auth/shouldSaveSiteAndDrawer'];
  const siteid = store.getters['auth/loggedInSiteId'];
  const cashDrawer = store.getters['auth/cashDrawer'];
  const cashdrawerid = cashDrawer.cashdrawerid;
  const hasWorkstationData = hasRequiredWorkstationData({ siteid, cashdrawerid });
  let workstationCookie = shouldSaveSiteAndDrawer ? getWorkstationCookie() : null;

  if (workstationCookie) {
    // Ensure necessary site/cash drawer cookie data hydrates state if not present
    if (!hasWorkstationData) {
      // Missing workstation data in state, but exists in cookie
      store.dispatch('auth/setWorkstationData', workstationCookie);
    }
  }

  if (shouldSaveSiteAndDrawer) {
    // Attempt to create workstation cookie if somehow it doesn't exist
    if (!workstationCookie && hasWorkstationData) {
      setWorkstationCookie({ siteid, cashdrawerid });
      workstationCookie = getWorkstationCookie();
    }

    // If state is missing workstation data and cookie doesn't exist, then somehow cookie data
    // was deleted or workstation data was never set in state (bad cookie?). In this case, we need to log the user out.
    if (isAuthenticated && !workstationCookie && !hasWorkstationData) {
      isAuthenticated = false;
    }
  } else {
    deleteWorkstationCookie();
  }

  // User must be authenticated or a cookie must be created or exist to proceed (if saving site and drawer)
  if (isAuthenticated && (!shouldSaveSiteAndDrawer || workstationCookie)) {
    if (isPreviousUser && !store.getters['auth/timeoutId']) await store.dispatch('auth/refreshToken');
    if (cacheDirtyTrackingFlag) {
      // When leaving route, coerce Order to CustomerOrder and if cached data exists for route, then update dirty flag.
      const lookupClass = className === 'Order' ? 'CustomerOrder' : className;
      const previousScreenData = store.getters['cached/previousScreenData'][lookupClass];
      if (previousScreenData) store.dispatch('cached/updateCachedDirtyFlag', { className: lookupClass, isDirty: store.getters['editObject/isRecordDirty'] });
    }

    // Check if route exists
    const whitelistedRouteNames = ['login', 'welcome', '404'];
    if (!whitelistedRouteNames.includes(to.name)) {
      const matchedRoute = router.resolve(to.path);
      const validClassName = !_isEmpty(store.getters['meta/class_schema'](className));
      if (!matchedRoute || _get(to, 'meta.dynaform') && !validClassName) {
        next({ path: '/404' });
        return;
      }
    }

    if (!_get(store.getters['permissions/permissions'], 'length')) {
      const { permissions: userPerms } = store.getters['auth/auth'];
      store.dispatch('permissions/initializePermissions', userPerms);
    }

    // Determine if the user has permission to access the routed screen, and if so, set permissions as necessary
    if (className !== 'Order' && className !== 'CustomerOrder' && to.name !== 'welcome' && to.name !== 'login') {
      const hasAllPerms = store.getters['permissions/allPermsPresent'];
      const formattedClassName = className.toLowerCase();
      const classMeta = store.getters['meta/class_schema'](className);
      const { explicitpermissions } = _get(classMeta, 'table_meta.info', {});

      let rejectRoute = false;
      const metaPermission = to.meta.permission;
      const permissionFunc = to.meta.allPerms ? hasAllPerms : hasPerm;
      // Check for hardcoded permission as defined in router.js
      if (metaPermission && !permissionFunc(metaPermission)) {
        rejectRouteHandler(from, next, metaPermission);
        rejectRoute = true;
        // Check for generic menu permission
      } else if (!metaPermission && className && !hasPerm(`menu${formattedClassName}`)) {
        rejectRouteHandler(from, next, `menu${formattedClassName}`);
        rejectRoute = true;
        /**
         * Reject navigation to new route if missing edit permissions,
         * or if allowinsert is false.
         *
         * This is preferable to being allowed to type up an entire new record
         * only to be told you don't have the permissions to save the record.
         */
      } else if (to.path.endsWith('/new')) {
        const allowInsert = _get(classMeta, 'table_meta.info.allowinsert', true) && !_get(classMeta, 'table_meta.info.readonly', false) ||
          ALLOWED_NEW_EXCEPTIONS.includes(className);
        if (!allowInsert) {
          next({ path: '/404' });
          rejectRoute = true;
        } else if (!metaPermission && explicitpermissions && !hasPerm(`edit${formattedClassName}`)) {
          rejectRoute = true;
          rejectRouteHandler(from, next, `edit${formattedClassName}`);
        }
      }

      if (rejectRoute) return;

      // Clear screen permissions if set
      if (!_isEmpty(store.getters['permissions/screenPermissions'])) {
        store.dispatch('permissions/setScreenPermissions', {});
      }

      if (cacheDirtyTrackingFlag) {
        // When leaving route, coerce Order to CustomerOrder and if cached data exists for route, then update dirty flag.
        const lookupClass = className === 'Order' ? 'CustomerOrder' : className;
        const previousScreenData = store.getters['cached/previousScreenData'][lookupClass];
        if (previousScreenData) store.dispatch('cached/updateCachedDirtyFlag', { className: lookupClass, isDirty: store.getters['editObject/isRecordDirty'] });
      }

      payload = {
        queryPermission: true,
        editPermission: true,
        deletePermission: true,
      };

      /**
       * Until we are able to sunset Flex and rename permissions, we need an
       * exception for Invoices as it has non-standard permission names as well
       * as explicitpermissions, which confuses the framework at the component level.
       *
       * There is a permission map for the Invoice routes that manually sets these permissions
       * in the permissions store module, so we just skip the generic construction of the
       * explicit permissions.
       *
       * See issue #13498 (Remove Flex)
       */
      if (to.meta.className !== 'Invoice' && explicitpermissions) {
        payload.queryPermission = hasPerm(`query${formattedClassName}`);
        payload.editPermission = hasPerm(`edit${formattedClassName}`);
        payload.deletePermission = hasPerm(`delete${formattedClassName}`);
      }
    }
  } else {
    if (to.name !== 'login' && !isAuthenticated) {
      // Ignore remembering application's base route (i.e., welcome screen) since that's the default:
      if (to.name !== 'welcome') store.dispatch('auth/rememberEntryRoute', to);
      store.dispatch('auth/logout', { rememberEntryRoute: false });
    }
    if (isPreviousUser && isAuthenticated && !store.getters['auth/timeoutId']) await store.dispatch('auth/refreshToken');
  }

  const routePermissions = store.getters['permissions/hasRoutePermissions'](to.name);
  if (routePermissions) {
    for (const [key, value] of Object.entries(routePermissions)) {
      payload[key] = hasPerm(value);
    }
  }
  store.dispatch('permissions/setScreenPermissions', payload);

  let title = 'Loft';
  const metaTitle = _get(to, 'meta.title');
  const classLabel = store.getters['meta/singular_label'](className);
  if (metaTitle) title = metaTitle;
  else if (classLabel) {
    if (_get(to, 'params.id')) {
      const meta = store.getters['meta/class_schema'](className);
      const viewMode = _get(meta, 'table_meta.info.readonly') ? 'View' : 'Edit';
      title = `${classLabel} - ${viewMode}`;
    }
    else if (_get(to, 'path', '').includes('new')) title = `${classLabel} - New`;
    else title = `${classLabel} - Search`;
  }
  document.title = title;
  if (from.name !== to.name || from.path !== to.path) next();
};

const beforeResolveInterceptor = (to, from, next) => {
  if (!to.params.isCached && store.getters['editObject/isRecordDirty']) store.dispatch('editObject/setRecordIsDirty', false); // If dirty, reset dirty tracking when not from cache on any route transition
  next();
};

export {
  beforeInterceptor,
  beforeResolveInterceptor,
};
